<template>
  <form-layout
    title="Line items"
    save-label="Save changes"
    :is-loading="isFetching"
    :is-saving="isSaving"
    :has-changes="hasChanges"
    bottom-bar-stick-bottom
    @on-dismiss="discard"
    @on-save="onSubmit"
    @on-discard="dismiss"
  >
    <div class="line-items-job-details">
      <div v-if="jobDetailsMainParts.length > 0" class="job-details-main">
        <template v-for="(part, i) in jobDetailsMainParts">
          <span v-if="i > 0" :key="`divider-${i}`" class="divider" aria-hidden="true">·</span>
          <span :key="`part-${i}`">{{ part }}</span>
        </template>
      </div>

      <div v-if="job" class="job-duration">
        <div class="job-details-title">Planned</div>
        <div>{{ formattedPlannedTime }}</div>
        <div class="text-right" data-test="job-planned-duration">
          {{ formattedPlannedDuration }}
        </div>
        <div class="job-details-title">{{ formattedActualTime.label }}</div>
        <div>{{ formattedActualTime.value }}</div>
        <div class="text-right" data-test="job-actual-duration">{{ formattedActualDuration }}</div>
      </div>

      <div v-if="displayInventory.length > 0" class="job-inventory">
        <div class="job-details-title">Inventory</div>
        <div class="job-inventory-items">
          <template v-for="(inv, i) in displayInventory">
            <span :key="`quantity-${i}`">{{ inv.quantity }} ×</span>
            <span :key="`name-${i}`" class="job-inventory-item-name">
              <span>{{ inv.inventorySubTypeName }}</span>
              <span class="divider" aria-hidden="true">·</span>
              <span>{{ inv.inventoryTypeName }}</span>
            </span>
          </template>
        </div>
      </div>
    </div>

    <vs-form ref="form">
      <line-item-form-card
        v-for="(lineItem, index) in formData"
        :key="lineItem.id"
        :index="index + 1"
        :line-item="lineItem"
        :hub-id="props.hubId"
        :assigned-to-service-provider-id="props.assignedToServiceProviderId"
        :price-book-items="priceBookItems"
        :is-loading-price-book="isLoadingPriceBook"
        :show-rates="hasLineItemRateAccess"
        :can-duplicate="!isAtMaxLineItems"
        :is-billable="isLineItemBillable(lineItem)"
        :show-billing-controls="hasLineItemFieldsEntitlement"
        :worker-options="getWorkerOptions(lineItem)"
        :default-worker-id="getDefaultWorkerId(lineItem.id)"
        :default-work-date="defaultWorkDate"
        @update="(changes) => updateLineItem(lineItem, changes)"
        @duplicate-item="duplicateLineItem(lineItem, index)"
        @toggle-billable="toggleLineItemBillable(lineItem)"
        @remove-item="deleteLineItem(lineItem)"
      />

      <vs-button
        :loading="isSaving"
        :disabled="isAtMaxLineItems"
        data-test="line-items-add-button"
        icon="add"
        label="Add line item"
        type="secondary"
        full-width
        @click="addLineItem()"
      />
    </vs-form>

    <template #bottom-bar-summary>
      <div class="line-items-summary">
        <div class="line-items-summary-label">
          <span>Billable total</span>
          <span class="line-items-count" data-test="line-items-form-count">
            {{ lineItemCountLabel }}
          </span>
        </div>
        <span
          v-if="hasLineItemRateAccess"
          class="line-items-total"
          data-test="line-items-form-total"
        >
          {{ formattedLineItemsTotal }}
        </span>
      </div>
    </template>

    <template #snack-bar-wrapper>
      <vs-snackbar
        v-model="isSnackbarVisible"
        type="error"
        sticky
        data-test="line-items-form-snackbar"
      >
        One or more items failed to update.
      </vs-snackbar>
    </template>
  </form-layout>
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect } from 'vue'
import FormLayout from '@/components/common/FormLayout.vue'
import { LineItem, lineItemDetailsEqual, lineItemDetailsToJson } from '@/models/line-item'
import {
  bulkCreateJobLineItemMutation,
  bulkDeleteJobLineItemMutation,
  bulkUpdateJobLineItemMutation,
  getJobLineItemsQuery,
  getJobPriceBookItems,
} from '@/api/jobs'
import { v4 } from 'uuid'
import { isSameMinute, startOfDay } from 'date-fns'
import VsForm from '@/components/vision/VsForm.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import { isEqual, omit, sortBy, uniq } from 'lodash'
import VsButton from '@/components/vision/VsButton.vue'
import { AxiosResponse } from 'axios'
import { useStore } from 'vuex-composition-helpers'
import { useCurrencyFormatter } from '@/utils/number-formatting'
import { useQueryClient } from '@tanstack/vue-query'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import Job from '@/models/job'
import { useSuggestedInventory } from '@/components/tickets/StopsAndInventories/useInventoryReceiptSuggestedInventory'
import { JobStopType } from '@ironsight/types'
import { useInventoryTypes } from '@/composables/inventory-types/useInventoryTypes'
import { buildDisplayInventory } from '@/utils/inventories'
import { useLineItemsTotal } from '@/components/jobs/line-items/useLineItemsTotal'
import {
  canAddPositionedLineItem,
  getPersistedLineItemPosition,
} from '@/components/jobs/line-items/lineItemPosition'
import {
  type LineItemFieldChanges,
  roundQuantityInput,
  roundRateInput,
} from '@/components/jobs/line-items/line-item-input'
import LineItemFormCard from '@/components/jobs/line-items/LineItemFormCard.vue'
import {
  detailsForDuplicate,
  getDetailFieldsForType,
  pruneDetailsForType,
  resolveLineItemType,
  toWorkerOptions,
} from '@/components/jobs/line-items/line-item-type'
import type User from '@/models/user'
import { ADDITIONAL_LINE_ITEM_FIELDS_ENTITLEMENT, useGetAccountEntitlements } from '@/api/accounts'
import { formatDateTime, formatDurationAsHours, formatTimeRange } from '@/utils/date-helpers'

interface Props {
  jobId: string
  jobNumber: number
  assignedToServiceProviderId?: string | null
  hubId: string
  batchId?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  assignedToServiceProviderId: null,
  batchId: null,
})

const emit = defineEmits<{
  (e: 'updated'): void
}>()

const store = useStore()

const { data: lineItems, isLoading: isFetching } = getJobLineItemsQuery(props.jobId)
const { data: priceBookItems, isLoading: isLoadingPriceBook } = getJobPriceBookItems(props.jobId)

const { isError: isBulkCreateError, mutateAsync: bulkCreateAsync } = bulkCreateJobLineItemMutation({
  disableOnSuccessCallBack: true,
})

const { isError: isBulkUpdateError, mutateAsync: bulkUpdateAsync } = bulkUpdateJobLineItemMutation({
  disableOnSuccessCallBack: true,
})

const { isError: isBulkDeleteError, mutateAsync: bulkDeleteAsync } = bulkDeleteJobLineItemMutation({
  disableOnSuccessCallBack: true,
})

const getJobById = computed<(jobNumber: number) => Job>(() => store.getters['jobs/getById'])

const job = computed(() => getJobById.value(props.jobNumber))
const jobIdRef = computed(() => job.value?.uuid)

const formattedPlannedTime = computed(() =>
  formatTimeRange(job.value.startTime, job.value.endTime, 'to'),
)
/**
 * Copied from JobDurationsDisplay.vue to remove use of Moment.
 * @see {@link /src/IronSight.Web/ClientApp/src/components/tickets/JobDurationsDisplay.vue}
 */
const formattedActualTime = computed(() => {
  if (
    !job.value.activeTime ||
    (job.value.completedTime &&
      isSameMinute(job.value.activeTime.valueOf(), job.value.completedTime.valueOf()))
  ) {
    return { label: 'Completed', value: formatDateTime(job.value.completedTime) }
  }

  if (job.value.activeTime && !job.value.completedTime) {
    return { label: 'Activated', value: formatDateTime(job.value.activeTime) }
  }

  return {
    label: 'Executed',
    value: formatTimeRange(job.value.activeTime, job.value.completedTime, 'to'),
  }
})
const formattedPlannedDuration = computed(() =>
  formatDurationAsHours(job.value.estimatedDuration, true),
)
const formattedActualDuration = computed(() =>
  job.value.completedTime
    ? formatDurationAsHours(job.value.activeDuration, true)
    : formatDurationAsHours(job.value.calculatedActiveDuration, true),
)

const { suggestedInventory } = useSuggestedInventory(jobIdRef, null, JobStopType.DropOff)

const { allInventoryTypes } = useInventoryTypes()

const displayInventory = computed(() => {
  return buildDisplayInventory(suggestedInventory.value, allInventoryTypes.value ?? [])
})

const getCompanyByChildHubId = computed(() => store.getters['companies/getByChildHubId'])
const company = computed(() => getCompanyByChildHubId.value(job.value?.hubId))
// Only the parts that have a value, so dividers never lead, trail, or double up.
const jobDetailsMainParts = computed(() =>
  [
    job.value?.id != null ? `#${job.value.id}` : null,
    job.value?.activity?.taskName,
    company.value?.shortCode,
  ].filter((part): part is string => !!part),
)

const hasLineItemRateAccessMethod = computed<
  (serviceProviderId: string | null, hubId: string) => boolean
>(() => store.getters['permissions/hasLineItemRateAccess'])
const hasLineItemRateAccess = computed(() =>
  hasLineItemRateAccessMethod.value(props.assignedToServiceProviderId, props.hubId),
)

// The backend checks the same entitlement on the current user's organization
// before it saves the billable flag.
const { data: currentOrgEntitlements } = useGetAccountEntitlements()
const hasLineItemFieldsEntitlement = computed(
  () =>
    currentOrgEntitlements.value?.entitlements.includes(ADDITIONAL_LINE_ITEM_FIELDS_ENTITLEMENT) ??
    false,
)

const formatter = useCurrencyFormatter()
const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const formData = ref<LineItem[]>()

type WorkerUser = Pick<User, 'uuid' | 'id' | 'name'>

const getAllUsersForOrganization = computed<(organizationId: string) => WorkerUser[]>(
  () => store.getters['users/getAllUsersForOrganization'],
)
const allUsers = computed<Record<string, WorkerUser>>(() => store.getters['users/getAllUsers'])

const byName = (user: WorkerUser) => user.name.toLowerCase()

/** Every active user in the assigned provider's organization, sorted by name. */
const providerUsers = computed(() =>
  props.assignedToServiceProviderId
    ? getAllUsersForOrganization.value(props.assignedToServiceProviderId)
    : [],
)

/**
 * The job's operators who can be picked. The crew holds user sids, which
 * aren't unique across organizations, so each is resolved within the
 * assigned provider's users.
 */
const crewWorkers = computed(() =>
  (job.value?.crew ?? [])
    .map((sid) => providerUsers.value.find((user) => user.id === sid))
    .filter((user): user is WorkerUser => !!user),
)

/** The crew, then the rest of the assigned provider's users. */
const workerOptions = computed(() => toWorkerOptions(crewWorkers.value, providerUsers.value))

/**
 * The shared options, plus the item's own saved or copied worker when they
 * aren't one of them, so their name still shows without offering them to
 * every item.
 */
function getWorkerOptions(lineItem: LineItem) {
  const savedWorkerId = lineItems.value?.find((saved) => saved.id === lineItem.id)?.details.workerId
  const ownWorkers = uniq([savedWorkerId, lineItem.details.workerId])
    .map((workerId) => workerId && allUsers.value?.[workerId])
    .filter(
      (user): user is WorkerUser =>
        !!user &&
        !workerOptions.value.some((option) => 'value' in option && option.value === user.uuid),
    )
  if (ownWorkers.length === 0) return workerOptions.value
  return toWorkerOptions(crewWorkers.value, sortBy([...providerUsers.value, ...ownWorkers], byName))
}

/** The uuid of the job's operator, when exactly one is assigned and can be picked. */
const soleOperatorId = computed(() => {
  if (job.value?.crew?.length !== 1) return null
  return crewWorkers.value[0]?.uuid ?? null
})

/** Whether the item is labour, the only kind that shows the worker field. */
function showsWorker(lineItem: LineItem) {
  return getDetailFieldsForType(getLineItemType(lineItem)).includes('workerId')
}

/**
 * Only the job's first labour item gets the operator as a default: the first
 * new one, while no labour item is saved yet. It moves to the next new labour
 * item if that one changes type or is removed. A saved item's worker is
 * whatever was saved, unset included.
 */
const defaultWorkerLineItemId = computed(() => {
  if (!hasLineItemFieldsEntitlement.value || !soleOperatorId.value) return null
  const labourItems = formData.value?.filter(showsWorker) ?? []
  const isNew = (lineItem: LineItem) => lineItem.id.startsWith('new-')
  if (labourItems.some((lineItem) => !isNew(lineItem))) return null
  return labourItems.find(isNew)?.id ?? null
})

/**
 * Every work date field shows this until the user picks a date: the day an
 * active job was started, otherwise today.
 */
const defaultWorkDate = computed(() => {
  const activeTime = job.value?.isActive ? job.value.activeTime : null
  return startOfDay(activeTime ? activeTime.toDate() : new Date())
})

/** The type that decides which type-specific fields an item shows. */
function getLineItemType(lineItem: LineItem) {
  return resolveLineItemType(lineItem, priceBookItems.value)
}

function getDefaultWorkerId(lineItemId: string) {
  return lineItemId === defaultWorkerLineItemId.value ? soleOperatorId.value : null
}

const form = ref<typeof VsForm | null>(null)
const isSnackbarVisible = ref(false)
const isSaving = ref(false)
const queryClient = useQueryClient()

const hasChanges = computed(() => !isEqual(lineItems.value, formData.value))
const isAtMaxLineItems = computed(() => !canAddPositionedLineItem(formData.value?.length ?? 0))

const lineItemCount = computed(() => formData.value?.length ?? 0)
const lineItemCountLabel = computed(
  () => `${lineItemCount.value} item${lineItemCount.value === 1 ? '' : 's'}`,
)

// Reflects unsaved edits, and leaves out items marked non-billable.
const billableLineItems = computed(() => (formData.value ?? []).filter(isLineItemBillable))
const lineItemsTotal = useLineItemsTotal(billableLineItems)
const formattedLineItemsTotal = computed(() => formatter.format(lineItemsTotal.value))

watch(isBulkDeleteError, (isError) => {
  isSnackbarVisible.value = isError
})

watch(isBulkCreateError, (isError) => {
  isSnackbarVisible.value = isError
})

watch(isBulkUpdateError, (isError) => {
  isSnackbarVisible.value = isError
})

watchEffect(() => {
  if (!formData.value && lineItems.value) {
    formData.value = lineItems.value.map(
      (li) =>
        new LineItem({
          id: li.id,
          name: li.name,
          rate: li.rate,
          quantity: li.quantity,
          unit_of_measure: li.unitOfMeasure,
          price_book_item_id: li.priceBookItemId,
          created: li.created,
          position: li.position,
          matches_rate_agreement: li.matchesRateAgreement,
          type: li.type,
          billable: li.billable,
          details: lineItemDetailsToJson(li.details),
        }),
    )
  }
})

function discard() {
  let confirmed = true

  if (hasChanges.value) {
    const confirmation = window.confirm(
      'There are unsaved changes, are you sure you want to discard them?',
    )

    if (!confirmation) {
      confirmed = false
    }
  }

  if (confirmed) {
    dismiss()
  }
}

function dismiss() {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isLineItemBottomSheetVisible: false,
  })
}

function addLineItem() {
  if (formData.value && canAddPositionedLineItem(formData.value.length))
    formData.value.push(createNewLineItem())
}

async function onSubmit() {
  if (typeof form.value !== typeof VsForm || !(form.value as typeof VsForm).isValid()) {
    return
  }
  isSaving.value = true

  const actions: Promise<AxiosResponse>[] = []
  const formLineItems = formData.value ?? []
  const toDelete =
    lineItems.value?.filter((li) => !formLineItems.some((fd) => fd.id === li.id)) ?? []
  if (toDelete.length > 0) {
    actions.push(
      bulkDeleteAsync({
        jobId: props.jobId,
        lineItemIds: toDelete.map((li) => li.id),
      }),
    )
  }

  const indexedFormLineItems = formLineItems.map((fd, index) => ({
    fd,
    position: getPersistedLineItemPosition(index),
  }))

  const toUpdate = indexedFormLineItems.filter(({ fd, position }) =>
    lineItems.value?.some(
      (li) =>
        fd.id === li.id &&
        (fd.name !== li.name ||
          fd.rate !== li.rate ||
          fd.quantity !== li.quantity ||
          fd.priceBookItemId !== li.priceBookItemId ||
          fd.type !== li.type ||
          fd.billable !== li.billable ||
          !lineItemDetailsEqual(fd.details, li.details) ||
          li.position !== position),
    ),
  )
  if (toUpdate.length > 0) {
    const bulkUpdateRequest = toUpdate.map(({ fd, position }) => {
      const saved = lineItems.value?.find((li) => li.id === fd.id)
      return {
        id: fd.id,
        name: fd.priceBookItemId !== null ? null : fd.name,
        rate: roundRateInput(fd.rate),
        quantity: roundQuantityInput(fd.quantity),
        price_book_item_id: fd.priceBookItemId,
        position,
        type: lineItemRequestType(fd),
        billable: fd.billable,
        // Omitted unless edited: details aren't returned to users outside the
        // assigned provider, and sending their empty copy would wipe them.
        details:
          saved && lineItemDetailsEqual(fd.details, saved.details)
            ? undefined
            : lineItemDetailsToJson(fd.details),
      }
    })
    actions.push(bulkUpdateAsync({ jobId: props.jobId, request: bulkUpdateRequest }))
  }

  const toCreate = indexedFormLineItems.filter(({ fd }) => fd.id.startsWith('new-'))
  if (toCreate.length > 0) {
    const bulkCreateRequest = toCreate.map(({ fd, position }) => ({
      id: v4(),
      name: fd.priceBookItemId !== null ? null : fd.name,
      rate: roundRateInput(fd.rate),
      quantity: roundQuantityInput(fd.quantity),
      price_book_item_id: fd.priceBookItemId,
      position,
      type: lineItemRequestType(fd),
      billable: fd.billable,
      details: createRequestDetails(fd),
    }))
    actions.push(bulkCreateAsync({ jobId: props.jobId, request: bulkCreateRequest }))
  }
  try {
    await Promise.all(actions)
    isSaving.value = false

    if (!isBulkUpdateError.value || !isBulkCreateError.value || !isBulkDeleteError.value) {
      await Promise.all(
        [
          queryClient.invalidateQueries(['line-items', props.jobId]),
          props.batchId && queryClient.invalidateQueries(['invoice', props.batchId]),
        ].filter(Boolean),
      )
    }

    if (!isBulkCreateError.value && !isBulkUpdateError.value && !isBulkDeleteError.value) {
      emit('updated')
      dismiss()
    }
  } catch (_) {
    isSaving.value = false
  }
}

async function deleteLineItem(lineItem: LineItem) {
  if (formData.value) {
    const index = formData.value.findIndex((li) => li.id === lineItem.id)
    if (index > -1) formData.value.splice(index, 1)
  }
}

/** Saves the defaulted worker the user saw, since a saved item gets no default. */
function createRequestDetails(lineItem: LineItem) {
  const workerId = lineItem.details.workerId ?? getDefaultWorkerId(lineItem.id) ?? undefined
  const details = { ...lineItem.details, workerId }
  return lineItemDetailsEqual(details, {}) ? undefined : lineItemDetailsToJson(details)
}

function createNewLineItem(source?: LineItem) {
  return new LineItem({
    id: `new-${v4()}`,
    name: source?.name ?? '',
    rate: source?.rate ?? null,
    quantity: source?.quantity ?? null,
    unit_of_measure: source?.unitOfMeasure ?? null,
    price_book_item_id: source?.priceBookItemId ?? null,
    created: new Date(),
    position: null,
    matches_rate_agreement: null,
    type: source?.type ?? null,
    billable: source?.billable ?? true,
  })
}

function duplicateLineItem(lineItem: LineItem, index: number) {
  if (!formData.value || !canAddPositionedLineItem(formData.value.length)) return

  const duplicate = createNewLineItem(lineItem)
  // Carry over a defaulted worker too: the duplicate isn't the first labour
  // item, so it wouldn't get the default itself.
  const workerId = lineItem.details.workerId ?? getDefaultWorkerId(lineItem.id)
  duplicate.details = {
    ...detailsForDuplicate(lineItem.details),
    ...(workerId != null ? { workerId } : {}),
  }
  formData.value.splice(index + 1, 0, duplicate)
}

function updateLineItem(lineItem: LineItem, changes: LineItemFieldChanges) {
  const isPriceBookLinkChange =
    'priceBookItemId' in changes && changes.priceBookItemId !== lineItem.priceBookItemId

  Object.assign(lineItem, changes)

  // The picked type belongs to the custom item; linking or unlinking a price
  // book item drops it so a stale type doesn't reappear.
  if (isPriceBookLinkChange) {
    lineItem.type = null
  }

  // A new type can come from the picked type or the price book link, so keep
  // only the fields it also shows (e.g. work date from one hourly item to another).
  if (isPriceBookLinkChange || 'type' in changes) {
    lineItem.details = pruneDetailsForType(lineItem.details, getLineItemType(lineItem))
  }

  // Picking a different price book item resets the description to its default.
  if (isPriceBookLinkChange && lineItem.priceBookItemId) {
    lineItem.details = omit(lineItem.details, 'description')
  }
}

/** Like the name, the type is only sent for custom items; the backend copies a price book item's. */
function lineItemRequestType(lineItem: LineItem) {
  return lineItem.priceBookItemId !== null ? null : lineItem.type
}

function isLineItemBillable(lineItem: LineItem) {
  return lineItem.billable
}

function toggleLineItemBillable(lineItem: LineItem) {
  lineItem.billable = !lineItem.billable
}
</script>

<style scoped>
.line-items-job-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 4px 36px;
  font-family: var(--font-regular);
  color: var(--color-text-secondary);
}

.job-details-main,
.job-inventory-item-name {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  > span {
    text-box: trim-both cap alphabetic;
  }

  .divider {
    color: var(--color-text-tertiary);
  }
}

.job-duration {
  display: grid;
  grid-template-columns: min-content minmax(0, 1fr) max-content;
  gap: 12px;

  > div {
    text-box: trim-both cap alphabetic;
  }
}

.job-inventory {
  display: flex;
  flex-direction: column;
  gap: 12px;

  > div {
    text-box: trim-both cap alphabetic;
  }
}

.job-inventory-items {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 12px 8px;

  > span {
    text-box: trim-both cap alphabetic;
  }
}

.job-inventory-item-name {
  gap: 4px;
}

.line-items-summary {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  font-family: var(--font-regular);
}

.line-items-summary-label {
  display: flex;
  align-items: baseline;
  gap: 8px;
  color: var(--color-black);

  > span {
    text-box: trim-both cap alphabetic;
  }
}

.line-items-count {
  font-size: 0.875rem;
  color: var(--color-text-tertiary);
}

.line-items-total {
  font-size: 1.5rem;
  font-weight: 600;
  text-box: trim-both cap alphabetic;
  white-space: nowrap;
}

.job-details-title {
  font-weight: 600;
  color: var(--color-text-tertiary);
}
</style>
